<script setup lang="ts">
import { BookOpen, CircleCheck, ExternalLink, Loader2, Send } from 'lucide-vue-next'

import type { DnsConnectState, DnsConnectStatus, DnsProviderContext } from '@/composables/domainConfig'

const props = defineProps<{
  domain: string
  emailDomainId?: number
}>()

defineEmits<{
  (e: 'next'): void
  (e: 'back'): void
}>()

const { t, locale } = useI18n()
const { getDnsConnect, getDnsProvider, sendDnsInstructions, startDnsConnect } = useDomainConfig()
const { isSupportConnection } = useLocalStorage()

const MAILERSEND_GUIDE_URL = 'https://www.mailersend.com/help/how-to-verify-and-authenticate-a-sending-domain'

const providerContext = ref<DnsProviderContext | null>(null)
const isDetecting = ref(!!props.emailDomainId)

const detectedProvider = computed(() => providerContext.value?.provider ?? null)

const unknownState = computed<'not_found' | 'lookup_failed' | 'unrecognized'>(() => {
  if (!providerContext.value || providerContext.value.zone_lookup === 'lookup_failed') return 'lookup_failed'
  if (providerContext.value.zone_lookup === 'not_found') return 'not_found'
  return 'unrecognized'
})

const guideUrl = computed(() => {
  const docs = detectedProvider.value?.docs
  if (!docs) return MAILERSEND_GUIDE_URL
  return docs[locale.value] ?? docs.en ?? Object.values(docs)[0] ?? MAILERSEND_GUIDE_URL
})

const FAILED_STATUSES: DnsConnectStatus[] = ['refused', 'failed', 'rollback_failed']

const isOneClickAvailable = ref(false)
const isConnecting = ref(false)
const setupMode = ref<'auto' | 'manual'>('auto')
const lastConnection = ref<DnsConnectState['connection']>(null)

const lastFailure = computed(() => {
  const connection = lastConnection.value
  if (!connection || !FAILED_STATUSES.includes(connection.status)) return null

  return {
    title: t(`pages.domain.config.dnsConnect.status.${connection.status}.title`),
    description:
      connection.status === 'failed'
        ? t(`pages.domain.config.dnsConnect.errors.${connection.error ?? 'unknown'}`, { zone: connection.zone })
        : t(`pages.domain.config.dnsConnect.status.${connection.status}.description`, { zone: connection.zone })
  }
})

const oneClickBenefits = computed(() => [
  t('pages.domain.config.dnsConnect.benefits.records'),
  t('pages.domain.config.dnsConnect.benefits.safe'),
  t('pages.domain.config.dnsConnect.benefits.scoped', { domain: props.domain })
])

const detectProvider = async () => {
  if (!props.emailDomainId) return

  isDetecting.value = true
  providerContext.value = await getDnsProvider(props.emailDomainId)

  if (isSupportConnection.value && detectedProvider.value?.key === 'ovh') {
    const state = await getDnsConnect(props.emailDomainId)
    isOneClickAvailable.value = state?.available ?? false
    lastConnection.value = state?.connection ?? null
    if (lastFailure.value) setupMode.value = 'manual'
  }

  isDetecting.value = false
}

const handleOneClick = async () => {
  if (!props.emailDomainId) return

  isConnecting.value = true
  const validationUrl = await startDnsConnect(props.emailDomainId)

  if (validationUrl) {
    window.location.href = validationUrl
    return
  }

  isConnecting.value = false
  setupMode.value = 'manual'
}

onMounted(detectProvider)

const isDelegateModalOpen = ref(false)
const delegateEmail = ref('')
const isSubmitting = ref(false)

const openDelegateModal = () => {
  delegateEmail.value = ''
  isDelegateModalOpen.value = true
}

const closeDelegateModal = () => {
  isDelegateModalOpen.value = false
  delegateEmail.value = ''
}

const handleDelegateSubmit = async () => {
  if (!delegateEmail.value || !props.emailDomainId) return

  isSubmitting.value = true
  const result = await sendDnsInstructions(props.emailDomainId, delegateEmail.value)
  isSubmitting.value = false

  if (result) closeDelegateModal()
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <div>
      <h3 class="text-lg font-semibold text-foreground">
        {{ t('pages.domain.config.steps.provider.title') }}
      </h3>
      <i18n-t
        keypath="pages.domain.config.steps.provider.description"
        tag="p"
        class="text-sm text-muted-foreground mt-1"
      >
        <template #domain>
          <strong class="font-semibold text-foreground">{{ domain }}</strong>
        </template>
      </i18n-t>
    </div>

    <BaseTip v-if="isDetecting" severity="secondary" class="custom_message">
      <template #icon>
        <Loader2 class="animate-spin" />
      </template>
      <template #text>
        {{ t('pages.domain.config.steps.provider.detected.loading') }}
      </template>
    </BaseTip>

    <template v-else-if="isOneClickAvailable && detectedProvider">
      <BaseTip v-if="lastFailure" severity="warn" class="custom_message">
        <template #text>
          <div class="flex flex-col gap-1">
            <span class="font-medium">{{ lastFailure.title }}</span>
            <span>{{ lastFailure.description }}</span>
          </div>
        </template>
      </BaseTip>

      <AppRadioCard v-model="setupMode" :options="['auto', 'manual']" class="sm:grid-cols-2">
        <template #0>
          <div class="flex flex-col gap-3">
            <div class="flex flex-wrap items-center gap-2">
              <span class="font-semibold text-foreground">
                {{ t('pages.domain.config.dnsConnect.choice.auto.title') }}
              </span>
              <BaseChip :text="t('pages.domain.config.dnsConnect.recommended')" color="green" size="sm" />
              <span class="text-xs text-muted-foreground">
                {{ t('pages.domain.config.dnsConnect.choice.auto.duration') }}
              </span>
            </div>
            <p class="text-sm text-muted-foreground">
              {{ t('pages.domain.config.dnsConnect.description', { provider: detectedProvider.name }) }}
            </p>
            <ul class="flex flex-col gap-2 text-sm text-foreground">
              <li v-for="benefit in oneClickBenefits" :key="benefit" class="flex items-center gap-2">
                <CircleCheck class="h-4 w-4 shrink-0 text-green-600" />
                {{ benefit }}
              </li>
            </ul>
          </div>
        </template>
        <template #1>
          <div class="flex flex-col gap-3">
            <div class="flex flex-wrap items-center gap-2">
              <span class="font-semibold text-foreground">
                {{ t('pages.domain.config.dnsConnect.choice.manual.title') }}
              </span>
              <span class="text-xs text-muted-foreground">
                {{ t('pages.domain.config.dnsConnect.choice.manual.duration') }}
              </span>
            </div>
            <p class="text-sm text-muted-foreground">
              {{ t('pages.domain.config.dnsConnect.choice.manual.description', { provider: detectedProvider.name }) }}
            </p>
          </div>
        </template>
      </AppRadioCard>

      <div v-if="setupMode === 'manual'" class="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" as="a" :href="guideUrl" target="_blank" rel="noopener">
          <BookOpen class="h-4 w-4" />
          {{ t('pages.domain.config.steps.provider.detected.guideButton') }}
        </Button>
        <Button variant="outline" size="sm" as="a" :href="detectedProvider.manager_url" target="_blank" rel="noopener">
          <ExternalLink class="h-4 w-4" />
          {{ t('pages.domain.config.steps.provider.detected.managerButton', { provider: detectedProvider.name }) }}
        </Button>
        <Button variant="outline" size="sm" @click="openDelegateModal">
          <Send class="h-4 w-4" />
          {{ t('pages.domain.config.steps.provider.delegateButton') }}
        </Button>
      </div>
    </template>

    <BaseTip
      v-else
      :severity="unknownState === 'not_found' && !detectedProvider ? 'error' : 'info'"
      class="custom_message"
    >
      <template #text>
        <div class="flex flex-col gap-3">
          <div class="flex flex-col gap-1">
            <template v-if="detectedProvider">
              <span class="font-medium">
                {{ t('pages.domain.config.steps.provider.detected.title', { provider: detectedProvider.name }) }}
              </span>
              <span>
                {{ t('pages.domain.config.steps.provider.detected.description', { provider: detectedProvider.name }) }}
              </span>
            </template>
            <template v-else-if="unknownState === 'unrecognized'">
              <span class="font-medium">{{ t('pages.domain.config.steps.provider.detected.unknownTitle') }}</span>
              <span>
                {{ t('pages.domain.config.steps.provider.detected.unknownDescription') }}
                <span class="font-mono">{{ providerContext?.nameservers.join(', ') }}</span>
              </span>
            </template>
            <template v-else-if="unknownState === 'not_found'">
              <span class="font-medium">
                {{ t('pages.domain.config.steps.provider.detected.notFoundTitle', { domain }) }}
              </span>
              <span>{{ t('pages.domain.config.steps.provider.detected.notFoundDescription') }}</span>
            </template>
            <template v-else>
              <span class="font-medium">{{ t('pages.domain.config.steps.provider.detected.failedTitle') }}</span>
              <span>{{ t('pages.domain.config.steps.provider.detected.failedDescription') }}</span>
            </template>
          </div>

          <div class="flex flex-wrap gap-2">
            <Button variant="default-primary" size="sm" as="a" :href="guideUrl" target="_blank" rel="noopener">
              <BookOpen class="h-4 w-4" />
              {{
                detectedProvider
                  ? t('pages.domain.config.steps.provider.detected.guideButton')
                  : t('pages.domain.config.steps.provider.detected.genericGuideButton')
              }}
            </Button>
            <Button
              v-if="detectedProvider"
              variant="outline"
              size="sm"
              as="a"
              :href="detectedProvider.manager_url"
              target="_blank"
              rel="noopener"
              class="bg-white !border !border-border"
            >
              <ExternalLink class="h-4 w-4" />
              {{ t('pages.domain.config.steps.provider.detected.managerButton', { provider: detectedProvider.name }) }}
            </Button>
            <Button variant="outline" size="sm" class="bg-white !border !border-border" @click="openDelegateModal">
              <Send class="h-4 w-4" />
              {{ t('pages.domain.config.steps.provider.delegateButton') }}
            </Button>
          </div>
        </div>
      </template>
    </BaseTip>

    <div class="flex gap-3">
      <Button variant="outline" @click="$emit('back')">
        {{ t('pages.domain.config.buttons.back') }}
      </Button>
      <BaseSupportConnection v-if="isOneClickAvailable && detectedProvider && setupMode === 'auto'">
        <Button variant="default-primary" :disabled="isConnecting" @click="handleOneClick">
          <Loader2 v-if="isConnecting" class="h-4 w-4 animate-spin" />
          {{ t('pages.domain.config.dnsConnect.button', { provider: detectedProvider.name }) }}
        </Button>
      </BaseSupportConnection>
      <Button v-else variant="default-primary" @click="$emit('next')">
        {{
          isOneClickAvailable
            ? t('pages.domain.config.steps.provider.showRecords')
            : t('pages.domain.config.steps.provider.accessButton')
        }}
      </Button>
    </div>

    <BaseModal
      :is-open="isDelegateModalOpen"
      :heading-title="t('pages.domain.config.steps.provider.delegate.title')"
      size="lg"
      :show-footer="false"
      @close="closeDelegateModal"
    >
      <div class="flex flex-col gap-6">
        <p class="text-sm text-foreground-secondary">
          {{ t('pages.domain.config.steps.provider.delegate.description') }}
        </p>

        <AppFormInput
          id="delegateEmail"
          v-model="delegateEmail"
          :label="t('pages.domain.config.steps.provider.delegate.emailLabel')"
          type="email"
          placeholder="exemple@entreprise.fr"
          name="delegateEmail"
          required
        />

        <div class="flex justify-end gap-3">
          <Button variant="outline" @click="closeDelegateModal">
            {{ t('pages.domain.config.buttons.cancel') }}
          </Button>
          <Button variant="default-primary" :disabled="!delegateEmail || isSubmitting" @click="handleDelegateSubmit">
            <Loader2 v-if="isSubmitting" class="h-4 w-4 animate-spin" />
            {{ t('pages.domain.config.steps.provider.delegate.submitButton') }}
          </Button>
        </div>
      </div>
    </BaseModal>
  </div>
</template>
